"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileManager = void 0;
var media_platform_1 = require("../../types/media-platform/media-platform");
var logger_1 = require("../../utils/deprecated/logger");
var job_1 = require("./job/job");
var job_observable_1 = require("./job/job-observable");
var file_descriptor_1 = require("./metadata/file-descriptor");
var file_metadata_1 = require("./metadata/file-metadata");
var list_files_response_1 = require("./responses/list-files-response");
/**
 * @param {Configuration} configuration
 * @param {HTTPClient} httpClient
 * @param {FileUploader} fileUploader
 * @doc FileManager
 */
var FileManager = /** @class */ (function () {
    function FileManager(configuration, httpClient, fileUploader) {
        this.configuration = configuration;
        this.httpClient = httpClient;
        this.fileUploader = fileUploader;
        this.queueFileUpload = undefined;
        /**
         * @type {string}
         */
        this.baseUrl = 'https://' + configuration.domain;
        /**
         * @type {string}
         */
        this.apiUrl = this.baseUrl + '/_api/files';
    }
    /**
     * Get Upload URL
     * @param {IUploadUrlRequest} uploadUrlRequest
     * @returns {Promise<UploadUrlResponse>}
     * @example
     * fileManager.getUploadUrl({
     *   mimeType: 'image/gif',
     *   path: '/path/to',
     *   size: 12345, // in bytes
     *   acl: 'public'
     * })
     * .then(response => {
     *   console.log(response.uploadToken, response.uploadUrl);
     * });
     */
    FileManager.prototype.getUploadUrl = function (uploadUrlRequest) {
        return this.fileUploader.getUploadUrl(uploadUrlRequest);
    };
    /**
     * Get Upload Configuration
     * @param {IUploadConfigurationRequest} uploadConfigurationRequest
     * @param version
     * @returns {Promise<UploadConfigurationResponse>}
     * @example
     * fileManager.getUploadConfiguration({
     *   mimeType: 'image/gif',
     *   path: '/path/to',
     *   size: 12345, // in bytes
     *   acl: 'public'
     * }).
     * then(response => {
     *   console.log(response.uploadToken, response.uploadUrl)
     * });
     */
    FileManager.prototype.getUploadConfiguration = function (uploadConfigurationRequest, version) {
        if (version === void 0) { version = 'v2'; }
        return this.fileUploader.getUploadConfiguration(uploadConfigurationRequest, version);
    };
    FileManager.prototype.doUploadFile = function (_a) {
        var path = _a.path, file = _a.file, uploadFileRequest = _a.uploadFileRequest, uploadToken = _a.uploadToken, uploadUrl = _a.uploadUrl, version = _a.version, _b = _a.logger, logger = _b === void 0 ? logger_1.dummyLogger : _b;
        var uploadFileResult = this.fileUploader.uploadFile({
            path: path,
            file: file,
            uploadFileRequest: uploadFileRequest,
            uploadToken: uploadToken,
            uploadUrl: uploadUrl,
            version: version,
            logger: logger,
        });
        // TODO: do it in a right way
        // Browser file uploader return `UploadJob` instead of promise
        if (!uploadFileResult.then) {
            return uploadFileResult;
        }
        return uploadFileResult.then(function (response) {
            return response;
        }, function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @description upload a file
     * @param {string} path the destination to which the file will be uploaded
     * @param {string|Buffer|Stream} file can be one of: string - path to file, memory buffer, stream
     * @param {UploadFileRequest?} uploadFileRequest
     * @param {string?} uploadToken
     * @param {string?} uploadUrl
     * @param {string?} version
     */
    FileManager.prototype.uploadFile = function (path, file, uploadFileRequest, uploadToken, uploadUrl, version) {
        if (version === void 0) { version = 'v2'; }
        if (typeof path === 'object') {
            return this.doUploadFile(path);
        }
        return this.doUploadFile({
            path: path,
            file: file,
            uploadFileRequest: uploadFileRequest,
            uploadToken: uploadToken,
            uploadUrl: uploadUrl,
            version: version,
        });
    };
    /**
     * @description upload a file
     * @param {string} path the destination to which the file will be uploaded
     * @param {string|Buffer|Stream} file can be one of: string - path to file, memory buffer, stream
     * @param {UploadFileRequest?} uploadFileRequest
     * @param {string?} uploadUrl
     */
    FileManager.prototype.uploadFileV3 = function (path, file, uploadFileRequest, uploadUrl) {
        return this.uploadFile(path, file, uploadFileRequest, undefined, uploadUrl, 'v3');
    };
    /**
     * import a file from a source URL, returns a Job Observable
     * @param importFileRequest
     * @returns {Observable<Job<FileImportSpecification>>}
     */
    FileManager.prototype.importFileObservable = function (importFileRequest) {
        var _this = this;
        return job_observable_1.observeJobCreator(this.configuration, this.httpClient)(function () { return _this.importFile(importFileRequest); });
    };
    /**
     * @description import a file from a source URL, returns a Job (see job manager)
     * @param {ImportFileRequest} importFileRequest
     * @returns {Promise<Job<FileImportSpecification>>}
     */
    FileManager.prototype.importFile = function (importFileRequest) {
        return this.httpClient
            .post(this.baseUrl + "/_api/import/file", importFileRequest)
            .then(function (response) {
            return new job_1.Job(response.payload);
        }, function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @description creates a file descriptor
     * @param {FileDescriptor} fileDescriptor
     * @returns {Promise<FileDescriptor>}
     */
    FileManager.prototype.createFile = function (fileDescriptor) {
        return this.httpClient
            .post(this.apiUrl, fileDescriptor)
            .then(function (response) {
            return new file_descriptor_1.FileDescriptor(response.payload);
        }, function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @description creates a folder descriptor, use this to create an empty directory
     * @param {FolderDescriptor} folderDescriptor
     * @returns {Promise<FileDescriptor>}
     */
    FileManager.prototype.createFolder = function (_a) {
        var acl = _a.acl, path = _a.path;
        return this.httpClient
            .post(this.apiUrl, {
            acl: acl,
            mimeType: media_platform_1.DescriptorMimeType.Folder,
            path: path,
            size: 0,
            type: media_platform_1.FileType.FOLDER,
        })
            .then(function (response) {
            return new file_descriptor_1.FileDescriptor(response.payload);
        }, function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @param {string} path
     */
    FileManager.prototype.getFile = function (path) {
        var params = {
            path: path,
        };
        return this.httpClient
            .get(this.apiUrl, params)
            .then(function (response) {
            return new file_descriptor_1.FileDescriptor(response.payload);
        }, function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @param {string} fileId
     * @returns {Promise<FileMetadata>}
     */
    FileManager.prototype.getFileMetadataById = function (fileId) {
        return this.httpClient
            .get(this.apiUrl + "/" + fileId + "/metadata")
            .then(function (response) {
            return new file_metadata_1.FileMetadata(response.payload);
        }, function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @param {string} path
     * @param {IListFilesRequest?} listFilesRequest
     */
    FileManager.prototype.listFiles = function (path, listFilesRequest) {
        var params = __assign({ path: path }, listFilesRequest);
        return this.httpClient
            .get(this.apiUrl + "/ls_dir", params)
            .then(function (response) {
            return new list_files_response_1.ListFilesResponse(response.payload);
        }, function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @param {string} path
     */
    FileManager.prototype.deleteFileByPath = function (path) {
        var params = {
            path: path,
        };
        return this.httpClient.delete(this.apiUrl, params).catch(function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @param {string} id
     */
    FileManager.prototype.deleteFileById = function (id) {
        return this.httpClient.delete(this.apiUrl + "/" + id).catch(function (error) {
            return Promise.reject(error);
        });
    };
    /**
     * @param {UpdateFileACL} params
     * @returns {Promise}
     */
    FileManager.prototype.updateFileACL = function (params) {
        return this.httpClient
            .put(this.apiUrl, params)
            .then(function (response) {
            return new file_descriptor_1.FileDescriptor(response.payload);
        });
    };
    return FileManager;
}());
exports.FileManager = FileManager;
//# sourceMappingURL=file-manager.js.map